import {
  createAdmin,
  createEmployee,
  getCompanyUsers,
  getUserById,
  getUsers,
  updateUser,
} from "@/services";
import type { UpdateUserData } from "@/types/user.type";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Récupère les utilisateurs d'une entreprise (@GET /users/by-company)
export const useCompanyUsers = (
  companyId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
  }
) => {
  return useQuery({
    queryKey: ["companyUsers", companyId, params],
    queryFn: () => getCompanyUsers(companyId, params),
    enabled: !!companyId, // Ne fait la requête que si companyId existe
  });
};

// Récupère les utilisateurs (@GET /users)
export const useUsers = (
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    role?: "SUPERUSER" | "ADMIN" | "EMPLOYEE";
    companyId?: string;
  },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: ["users", params],
    queryFn: () => getUsers(params),
    enabled: options?.enabled ?? true,
  });
};

// Récupère un utilisateur par son ID (@GET /users/:id)
export const useUserById = (id: string) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: () => getUserById(id),
  });
};

// Crée un administrateur (@POST /users)
export const useCreateAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAdmin, //createAdmin service function pour créer un administrateur
    onSuccess: () => {
      // Invalider le cache des utilisateurs
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["companyUsers"] });
    },
  });
};

// Crée un employé (@POST /users/employee)
export const useCreateEmployee = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEmployee, //createEmployee service function pour créer un employé
    onSuccess: () => {
      // Invalider le cache des utilisateurs
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["companyUsers"] });
      queryClient.invalidateQueries({ queryKey: ["companyStats"] })
    },
  });
};

// Met à jour un utilisateur (@PATCH /users)
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserData }) =>
      updateUser(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      // Invalider aussi les queries des utilisateurs d'entreprise pour mettre à jour l'affichage
      queryClient.invalidateQueries({ queryKey: ["companyUsers"] });
      queryClient.invalidateQueries({ queryKey: ["companyStats"] })
    },
  });
};


